import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Musteri } from '@nero/shared';
import { api, sorgu } from '../lib/api.js';
import { Simge } from './Simge.js';

/**
 * MÜŞTERİ SEÇİCİ — yaz, listeden gelsin, Enter'la seç.
 *
 * ─── NİÇİN VAR ───
 * Önceki düzen bir ARAMA KUTUSU ile onun altındaki KAPALI bir açılır
 * listeden oluşuyordu. Kullanıcı "Ahmet" yazınca liste gerçekten daralıyordu
 * — ama liste KAPALI olduğu için ekranda hiçbir şey değişmiyordu; kutuda
 * hâlâ "İsteğe bağlı" yazıyordu. Ölçüldü: arama sonrası listede tek seçenek
 * kalıyor, seçili değer boş kalıyor. Kullanıcının sözü tam da buydu:
 * "isim yazdığımda otomatik müşteriyi bulmuyor".
 *
 * Dahası, Kasa penceresinde Enter formu KAYDEDİYORDU: arama kutusuna
 * "Ahmet" yazıp Enter'a basınca müşteri seçilmiyor, müşterisiz bir kasa
 * kaydı sessizce oluşuyordu (ölçüldü: 250 ₺'lik kayıt, Müşteri "—").
 *
 * ─── NE YAPAR ───
 *   · Yazıldıkça eşleşenler AÇIK bir listede, kutunun hemen altında görünür
 *   · ↑ ↓ ile gezilir, Enter seçer, Esc kapatır
 *   · Enter bu kutunun içinde ASLA formu kaydetmez (olay yukarı taşınmaz)
 *   · Seçim İSTEĞE BAĞLIDIR: seçili müşteri × ile kaldırılır
 *   · Sunucu sonuçları YAKINLIĞA göre sıralar; en üstteki en olası adaydır
 */

/** Arama isteği bu kadar sessizlikten sonra gider (ms). */
const BEKLEME = 220;
/** Listede en çok bu kadar satır gösterilir. */
const AZAMI = 8;
/** Liste bundan yükseğe çıkmaz (px) — yer varken bile ekranı kaplamasın. */
const AZAMI_YUKSEKLIK = 268;
/** Bundan az yer kalmışsa liste diğer yöne açılır (px). */
const ASGARI_YUKSEKLIK = 132;

/**
 * Kutunun içinde bulunduğu KAYAN kutuyu bulur (pencere gövdesi gibi).
 *
 * Bu önemli: `overflow: auto` olan bir kutunun içindeki mutlak konumlu
 * öğe, z-index ne olursa olsun o kutu tarafından KIRPILIR. Liste bu yüzden
 * kendini o kutuya göre ölçer; yoksa pencerenin alt kenarında kesilir.
 */
function kayanAta(el: HTMLElement | null): HTMLElement | null {
  for (let p = el?.parentElement ?? null; p; p = p.parentElement) {
    const t = getComputedStyle(p).overflowY;
    if (t === 'auto' || t === 'scroll') return p;
  }
  return null;
}

export interface MusteriSeciciOzellikleri {
  /** Seçili müşteri kimliği — '' ise seçim yok. */
  deger: string;
  onChange: (id: string, musteri: Musteri | null) => void;
  etiket?: ReactNode;
  ipucu?: ReactNode;
  /** Seçim yokken kutunun altında görünen açıklama. */
  bosMetin?: string;
  yerTutucu?: string;
  autoFocus?: boolean;
  /** Düzenlemede seçili kaydın adı dışarıdan gelir (tek istek tasarrufu). */
  baslangicAdi?: string | null;
}

export function MusteriSecici({
  deger, onChange, etiket = 'Müşteri', ipucu, bosMetin,
  yerTutucu = 'Müşteri ara — isim, telefon veya plaka…',
  autoFocus, baslangicAdi,
}: MusteriSeciciOzellikleri) {
  const [metin, setMetin] = useState('');
  const [liste, setListe] = useState<Musteri[]>([]);
  const [acik, setAcik] = useState(false);
  const [vurgu, setVurgu] = useState(0);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [secilenAd, setSecilenAd] = useState<string | null>(baslangicAdi ?? null);
  /* Listenin nereye açılacağı ÖLÇÜLEREK bulunur; sabit değildir. */
  const [yerlesim, setYerlesim] = useState<{ yukari: boolean; enCok: number }>({ yukari: false, enCok: AZAMI_YUKSEKLIK });
  const sarmalayici = useRef<HTMLDivElement>(null);
  const kutuSatiri = useRef<HTMLDivElement>(null);
  /* Açılışta yapılan kaydırma bir KEZ yapılır; her tuşta değil. */
  const kaydirildi = useRef(false);
  const kutu = useRef<HTMLInputElement>(null);
  const listeId = useId();

  /* Seçili kaydın adı: seçim varken kutu yerine bir "rozet" gösterilir.
     Düzenlemede ad dışarıdan gelir; gelmezse tek seferlik çekilir. */
  useEffect(() => {
    if (!deger) { setSecilenAd(null); return; }
    if (secilenAd) return;
    let iptal = false;
    api.al<Musteri>(`/musteriler/${deger}`)
      .then((m) => { if (!iptal) setSecilenAd(m.ad); })
      /* Ad okunamazsa seçim GEÇERSİZ SAYILMAZ: kimlik doğru, yalnız
         etiket eksiktir. Kaydı bozmaktansa kimliği göstermek yeğdir. */
      .catch(() => { if (!iptal) setSecilenAd(`#${deger}`); });
    return () => { iptal = true; };
  }, [deger, secilenAd]);

  /* Yazıldıkça ara. Her tuşta istek gitmez; kısa bir sessizlik beklenir. */
  useEffect(() => {
    if (deger) return;                       /* seçim yapıldı, arama durur */
    let iptal = false;
    const zaman = window.setTimeout(() => {
      setYukleniyor(true);
      api.al<Musteri[]>(`/musteriler${sorgu({ q: metin })}`)
        .then((s) => { if (!iptal) { setListe(s); setVurgu(0); } })
        .catch(() => { if (!iptal) setListe([]); })
        .finally(() => { if (!iptal) setYukleniyor(false); });
    }, metin ? BEKLEME : 0);
    return () => { iptal = true; window.clearTimeout(zaman); };
  }, [metin, deger]);

  /**
   * LİSTE KIRPILMAZ — nereye sığıyorsa oraya açılır.
   *
   * Kasa penceresinde "Müşteri" alanı formun ALTINA yakındır. Liste sabit
   * biçimde aşağı açıldığında pencerenin kayan gövdesi onu kesiyordu:
   * ekranda yalnız ilk satırın yarısı görünüyordu (ölçüldü, ekran
   * görüntüsüyle). Burada kutunun üstünde ve altında kalan boşluk
   * ölçülür; liste hangisine sığıyorsa o yöne açılır ve yüksekliği o
   * boşluğa göre kısılır. Böylece liste her koşulda TAMAMEN görünür.
   */
  useLayoutEffect(() => {
    if (!acik || deger) return;
    const satir = kutuSatiri.current;
    if (!satir) return;
    const kap = kayanAta(satir);

    /* Liste AÇILIRKEN bir kez: aşağıda yer yoksa ve kutu daha yukarı
       kaydırılabiliyorsa, önce kaydır. Böylece liste alışıldık biçimde
       AŞAĞI açılır; yukarı açmak yalnızca son çare kalır. Yalnız açılışta
       yapılır — her tuş vuruşunda yapılsaydı ekran zıplardı. */
    if (kap && !kaydirildi.current) {
      kaydirildi.current = true;
      const r0 = satir.getBoundingClientRect();
      const kr0 = kap.getBoundingClientRect();
      /* Hedef, eşiğin TAM ÜSTÜ değil biraz üstüdür. Tam eşiğe kaydırmak
         ölçüldü: boşluk 131,6 px çıkıyor, 132 eşiğinin yarım piksel
         altında kalıyor ve liste gereksizce yukarı açılıyordu. */
      const eksik = ASGARI_YUKSEKLIK + 24 - (kr0.bottom - r0.bottom);
      const kalan = kap.scrollHeight - kap.clientHeight - kap.scrollTop;
      if (eksik > 0 && kalan > 0) kap.scrollTop += Math.min(eksik, kalan);
    }

    const r = satir.getBoundingClientRect();
    const kr = kap ? kap.getBoundingClientRect() : null;
    const ust = kr ? kr.top : 0;
    const alt = kr ? kr.bottom : window.innerHeight;
    const altBosluk = alt - r.bottom - 8;
    const ustBosluk = r.top - ust - 8;
    const yukari = altBosluk < ASGARI_YUKSEKLIK && ustBosluk > altBosluk;
    const bosluk = Math.max(yukari ? ustBosluk : altBosluk, 0);
    /* Yükseklik ASGARİ'ye YUVARLANMAZ. Her iki yönde de 50 px kalmışsa
       listeyi 132 px çizmek onu yine kırpardı; burada gerçekten kalan yer
       neyse o kullanılır — liste kısalır ama tamamı görünür ve kaydırılır. */
    setYerlesim({ yukari, enCok: Math.min(bosluk, AZAMI_YUKSEKLIK) });
  }, [acik, deger, liste.length, metin]);

  /* Dışarı tıklayınca liste kapanır. */
  useEffect(() => {
    if (!acik) return;
    function disariTikla(e: MouseEvent) {
      if (!sarmalayici.current?.contains(e.target as Node)) setAcik(false);
    }
    document.addEventListener('mousedown', disariTikla);
    return () => document.removeEventListener('mousedown', disariTikla);
  }, [acik]);

  const gosterilen = useMemo(() => liste.slice(0, AZAMI), [liste]);

  function sec(m: Musteri) {
    setSecilenAd(m.ad);
    setMetin('');
    setAcik(false);
    onChange(String(m.id), m);
  }

  function temizle() {
    setSecilenAd(null);
    setMetin('');
    onChange('', null);
    /* Odak kutuya döner: "yanlış müşteriyi seçtim" diyen biri hemen
       yeniden yazmaya başlayabilsin. */
    window.setTimeout(() => kutu.current?.focus(), 0);
  }

  function tus(e: React.KeyboardEvent<HTMLInputElement>) {
    /* ENTER BU KUTUDA FORMU KAYDETMEZ.
       Kasa penceresinde formun kendisinde "Enter ile hızlı kayıt" var; olay
       yukarı taşınırsa müşteri seçmeye çalışan kullanıcı kaydı gönderir.
       Bu yüzden Enter, Esc ve okların hepsi burada durdurulur. */
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const aday = gosterilen[vurgu] ?? gosterilen[0];
      if (aday) sec(aday);
      else setAcik(true);
      return;
    }
    if (e.key === 'Escape') {
      if (acik) { e.stopPropagation(); setAcik(false); }
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      setAcik(true);
      if (gosterilen.length === 0) return;
      setVurgu((v) => {
        const yeni = e.key === 'ArrowDown' ? v + 1 : v - 1;
        return (yeni + gosterilen.length) % gosterilen.length;
      });
    }
  }

  /* Liste kapanınca kaydırma izni tazelenir: bir sonraki açılışta yeniden
     ölçülüp gerekiyorsa yine kaydırılır. */
  if (!acik && kaydirildi.current) kaydirildi.current = false;

  const listeAcik = acik && !deger;

  return (
    <div className="alan musteri-secici" ref={sarmalayici}>
      {etiket && <label>{etiket}</label>}

      {deger ? (
        <div className="secili-musteri">
          <Simge ad="musteri" className="simge" />
          <span className="ad">{secilenAd ?? '…'}</span>
          <button type="button" className="kaldir" onClick={temizle} aria-label="Müşteri seçimini kaldır">
            <Simge ad="carpi" className="simge" />
          </button>
        </div>
      ) : (
        /* Liste BU satıra göre konumlanır, alanın tamamına göre DEĞİL:
           altındaki ipucu yazısı listeyi aşağı itmesin. */
        <div className="kutu-satiri" ref={kutuSatiri}>
          <div className="arama esnek">
            <Simge ad="ara" className="simge" />
            <input
              ref={kutu}
              className="girdi"
              type="text"
              role="combobox"
              aria-expanded={listeAcik}
              aria-controls={listeId}
              aria-autocomplete="list"
              autoComplete="off"
              autoFocus={autoFocus}
              value={metin}
              placeholder={yerTutucu}
              onChange={(e) => { setMetin(e.target.value); setAcik(true); }}
              onFocus={() => setAcik(true)}
              onKeyDown={tus}
            />
          </div>

          {listeAcik && (
            <div
              className={yerlesim.yukari ? 'secici-liste yukari' : 'secici-liste'}
              style={{ maxHeight: yerlesim.enCok }}
              id={listeId}
              role="listbox"
            >
              {gosterilen.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  role="option"
                  aria-selected={i === vurgu}
                  className={i === vurgu ? 'satir vurgulu' : 'satir'}
                  onMouseEnter={() => setVurgu(i)}
                  /* `mousedown` kullanılır: `click` odak kaybından SONRA gelir
                     ve dışarı-tıkla kapatması araya girerdi. */
                  onMouseDown={(e) => { e.preventDefault(); sec(m); }}
                >
                  <span className="ad">{m.ad}</span>
                  {m.telefon && <span className="yan">{m.telefon}</span>}
                </button>
              ))}
              {gosterilen.length === 0 && (
                <div className="satir bos">
                  {yukleniyor ? 'Aranıyor…'
                    : metin ? `"${metin}" için kayıtlı müşteri yok`
                      : 'Kayıtlı müşteri yok'}
                </div>
              )}
              {liste.length > AZAMI && (
                <div className="satir bos">+{liste.length - AZAMI} kayıt daha — aramayı daraltın</div>
              )}
            </div>
          )}
        </div>
      )}

      {ipucu ? <div className="ipucu">{ipucu}</div>
        : !deger && bosMetin ? <div className="ipucu">{bosMetin}</div> : null}
    </div>
  );
}
